import React from 'react';
import { useSelector } from 'react-redux';
import { Loading } from '@planne-software/uni';
import { useTranslate, useAppSelector } from '@src/core/hooks';
import { directLinkStateSelector } from '@src/stores/directLink';
import { currentSaleSelector } from '@src/stores/sale';
import { currentOrderSelector } from '@src/features/orders';
import Error from '@src/components/DirectLink/Error';
import FeatureFlag from '@src/components/FeatureFlag';
import { DirectLinkType, PAYMENT_STEP_DIRECT_LINK_TYPES } from '@src/domains/direct-links';
import { PaymentForm } from '@src/features/payments/legacy';
import { ConfirmationWait } from '@src/features/orders/awaiting-confirmation';
import { CheckoutWidget } from '@src/views/checkout';
import { usePurchaseSummary } from '@src/views/checkout/hooks/usePurchaseSummary';
import { Label, Value, ValueDisplay } from './styles';
import { SaleState } from '@src/core/buyerApiClient';
import { isTransactableSettled } from '@src/domains/transactables';

export const Payment = () => {
  const { directLink, error } = useSelector(directLinkStateSelector);
  const sale = useAppSelector(currentSaleSelector);
  const order = useAppSelector(currentOrderSelector);
  const isOrderLink = directLink?.type === DirectLinkType.Order;
  const { summary } = usePurchaseSummary();
  const { i18n } = useTranslate();

  const getFormattedValue = () =>
    (directLink!.totalAmountCents / 100).toLocaleString('pt-br', { style: 'currency', currency: 'BRL' });

  const getError = () => {
    if (directLink?.type === DirectLinkType.Sale) {
      if (sale?.state === SaleState.PaymentComplete || directLink?.status === 'payment_complete') {
        return i18n('direct_link_finished_payment_message');
      }
    }

    if (isOrderLink && isTransactableSettled({ order })) {
      return i18n('direct_link_finished_payment_message');
    }

    if (directLink?.status === 'expired' || directLink?.isExpired) {
      return i18n('direct_link_expired_message');
    }
    if (directLink?.status === 'payment_complete') {
      return i18n('direct_link_finished_payment_message');
    }
    if (directLink?.status === 'canceled') {
      return i18n('direct_link_cancelled_message');
    }
    if (error) {
      return error;
    }

    return false;
  };

  const totalDisplay = () => (
    <ValueDisplay>
      <Label>{i18n('checkout_totalValue')}</Label>
      <Value>{getFormattedValue()}</Value>
    </ValueDisplay>
  );

  // The legacy form draws its own total; the new checkout carries it in the summary sidebar.
  // A sale still waiting on the supplier is not payable, so the form would declare it expired: it waits here
  // instead, and the form takes over once the confirmed sale lands in the store.
  const legacyPayment = () => (
    <>
      {totalDisplay()}

      {sale?.state === SaleState.AwaitingConfirmation ? (
        <ConfirmationWait />
      ) : (
        <PaymentForm
          directLink={directLink}
          sale={isOrderLink ? undefined : sale}
          order={isOrderLink ? order : undefined}
          purchaseSummary={summary}
        />
      )}
    </>
  );

  if (getError()) {
    return (
      <>
        {totalDisplay()}

        <Error>{getError()}</Error>
      </>
    );
  }

  if (!PAYMENT_STEP_DIRECT_LINK_TYPES.includes(directLink?.type ?? '')) {
    return legacyPayment();
  }

  return (
    <FeatureFlag
      feature='PR-3043-checkout'
      pending={() => <Loading size='lg' />}
      active={() => <CheckoutWidget editable={false} skipResumeCart />}
      fallback={legacyPayment}
    />
  );
};

export default Payment;
